import React, { Suspense, useEffect, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { useRoutes } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import './App.scss';
import routes from './routes';
import { SnackbarProvider } from 'notistack';
import GlobalMessage from './components/message/GlobalMessage';
import ChatWrapper from './pages/chat';
import IdleLoginModal from './components/idleLoginModal';
import activityTracker from './utils/activityTracker';
import sessionHeartbeat from './utils/sessionHeartbeat';
import { userInfoStore } from './models';
import arms from '@/utils/arms';

function App() {
  const element = useRoutes(routes);
  // 未登录不启用闲置检测。token 在 authenticate 返回后即写入 store（observer 感知变化），
  // 因此选组织阶段（登录页内）也会检测；登录页填表单阶段（无 token）不检测。
  // 接线在 App 层而非 layout：layout 挂载于组织选择之前，无法覆盖选组织阶段的闲置检测。
  // 另需业务设置 IsIdleLogoutEnabled 开启（由布局挂载时 requestBusinessSetting 拉取）
  const showIdleUi = !!userInfoStore.token && userInfoStore.isIdleLogoutEnabled;
  const lightTheme = createTheme({
    typography: {
      htmlFontSize: 18,
      // fontSize: 14
    },
    palette: {
      mode: 'light',
      primary: {
        main: '#3f51b5',
      },
      secondary: {
        main: '#f50057',
      },
      background: {
        default: '#fafafa',
        paper: '#fff',
      },
    },
  });
  useEffect(() => {
    window.onerror = (msg, url, line, col, error) => {
      arms.error(error, {
        filename: url,
        lineno: line,
        colno: col,
      });
    };
  }, []);
  // 闲置重登接线：闲置触发 → 暂停判定并打开弹窗；重登成功 → 恢复；关闭 → 退出登录
  const [idleModalOpen, setIdleModalOpen] = useState(false);
  useEffect(() => {
    if (!showIdleUi) return;
    activityTracker.start();
    sessionHeartbeat.start();
    const unsub = activityTracker.onIdle(() => {
      activityTracker.pause();
      setIdleModalOpen(true);
    });
    return () => {
      unsub();
      sessionHeartbeat.stop();
      activityTracker.stop();
    };
  }, [showIdleUi]);
  return (
    <div id='App'>
      <ThemeProvider theme={lightTheme}>
        <SnackbarProvider anchorOrigin={{ horizontal: 'center', vertical: 'top' }}>
          <Suspense>{element}</Suspense>
          <GlobalMessage/>
          <ChatWrapper />
          {showIdleUi && (
            <IdleLoginModal
              open={idleModalOpen}
              onReloginSuccess={() => {
                activityTracker.resume();
                setIdleModalOpen(false);
              }}
              onClose={() => {
                userInfoStore.logout();
                window.location.replace('/');
              }}
            />
          )}
        </SnackbarProvider>
      </ThemeProvider>
    </div>
  );
}

export default observer(App);
